import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getBatches, quarantineBatch, releaseBatch } from '../lib/api';
import { Shield, ShieldCheck, ShieldWarning, Warning, CheckCircle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function Quarantine() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [holdDialog, setHoldDialog] = useState(null);
  const [holdReason, setHoldReason] = useState('QA inspection required');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getBatches();
      setBatches(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const submitHold = async () => {
    if (!holdDialog) return;
    try {
      await quarantineBatch(holdDialog.id, holdReason);
      toast.success(`Batch ${holdDialog.batch_number} quarantined`);
      setHoldDialog(null);
      setHoldReason('QA inspection required');
      fetchData();
    } catch (e) {
      toast.error('Failed to quarantine batch');
    }
  };

  const release = async (batch) => {
    if (!window.confirm(`Release batch ${batch.batch_number} back to available?`)) return;
    try {
      await releaseBatch(batch.id);
      toast.success(`Batch ${batch.batch_number} released`);
      fetchData();
    } catch (e) {
      toast.error('Failed to release batch');
    }
  };

  const quarantinedBatches = batches.filter(b => b.status === 'quarantine');
  const availableBatches = batches.filter(b => b.status !== 'quarantine');

  const BatchTable = ({ list, variant }) => (
    <div className="overflow-x-auto">
      <table className="w-full table-dense">
        <thead>
          <tr>
            <th>Batch #</th>
            <th>SKU</th>
            <th>Quantity</th>
            <th>Cost</th>
            <th>Received</th>
            <th>Status</th>
            {variant === 'hold' && <th>Reason</th>}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={variant === 'hold' ? 8 : 7} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
          ) : list.length === 0 ? (
            <tr><td colSpan={variant === 'hold' ? 8 : 7} className="text-center py-12">
              {variant === 'hold' ? (
                <>
                  <ShieldCheck size={40} className="mx-auto mb-2 text-green-400" />
                  <p className="text-muted-foreground">No batches on QA hold</p>
                </>
              ) : (
                <p className="text-muted-foreground">No batches found</p>
              )}
            </td></tr>
          ) : list.map(b => (
            <tr key={b.id} data-testid={`batch-row-${b.batch_number}`} className={b.status === 'quarantine' ? 'bg-red-50' : ''}>
              <td className="font-mono font-semibold">{b.batch_number}</td>
              <td>
                <p className="font-mono text-xs text-muted-foreground">{b.sku_code || '-'}</p>
                <p className="text-sm">{b.sku_name || '-'}</p>
              </td>
              <td className="font-mono font-bold">{b.quantity}</td>
              <td className="font-mono">${(b.cost_price || 0).toFixed(2)}</td>
              <td className="text-sm text-muted-foreground">
                {b.received_date ? new Date(b.received_date).toLocaleDateString() : '-'}
              </td>
              <td>
                <span className={`px-2 py-1 text-xs font-bold uppercase border ${
                  b.status === 'quarantine'
                    ? 'bg-red-100 text-red-800 border-red-200'
                    : 'bg-green-100 text-green-800 border-green-200'
                }`}>
                  {b.status || 'available'}
                </span>
              </td>
              {variant === 'hold' && (
                <td className="text-sm text-muted-foreground max-w-xs">
                  {b.quarantine_reason || '-'}
                </td>
              )}
              <td>
                {b.status === 'quarantine' ? (
                  <Button
                    size="sm"
                    onClick={() => release(b)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid={`release-${b.batch_number}`}
                  >
                    <ShieldCheck size={14} className="mr-1" /> Release
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    onClick={() => setHoldDialog(b)}
                    data-testid={`hold-${b.batch_number}`}
                  >
                    <ShieldWarning size={14} className="mr-1" /> Hold
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <Layout>
      <div data-testid="quarantine-page" className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Shield size={32} weight="fill" className="text-red-600" />
            QA Hold / Quarantine
          </h1>
          <p className="text-muted-foreground">Flag batches for inspection; quarantined batches are blocked from picking</p>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total Batches</p>
            <p className="text-3xl font-bold">{batches.length}</p>
          </div>
          <div className="bg-red-50 border border-red-200 p-4">
            <p className="text-xs uppercase tracking-widest text-red-700 font-bold">On QA Hold</p>
            <p className="text-3xl font-bold text-red-700">{quarantinedBatches.length}</p>
          </div>
          <div className="bg-green-50 border border-green-200 p-4">
            <p className="text-xs uppercase tracking-widest text-green-700 font-bold">Available for Pick</p>
            <p className="text-3xl font-bold text-green-700">{availableBatches.length}</p>
          </div>
        </div>

        <Tabs defaultValue="hold" className="bg-card border border-border rounded-lg">
          <TabsList className="w-full border-b border-border p-0 h-auto bg-transparent rounded-none">
            <TabsTrigger value="hold" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-red-600">
              <Warning size={16} className="mr-2" /> On Hold ({quarantinedBatches.length})
            </TabsTrigger>
            <TabsTrigger value="avail" className="flex-1 rounded-none data-[state=active]:bg-muted data-[state=active]:border-b-2 data-[state=active]:border-green-600">
              <CheckCircle size={16} className="mr-2" /> Available ({availableBatches.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="hold" className="p-0 m-0">
            <BatchTable list={quarantinedBatches} variant="hold" />
          </TabsContent>
          <TabsContent value="avail" className="p-0 m-0">
            <BatchTable list={availableBatches} variant="avail" />
          </TabsContent>
        </Tabs>

        {/* Hold dialog */}
        <Dialog open={!!holdDialog} onOpenChange={(o) => !o && setHoldDialog(null)}>
          <DialogContent className="border border-border rounded-none" data-testid="hold-dialog">
            <DialogHeader>
              <DialogTitle>
                Quarantine Batch {holdDialog?.batch_number}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 text-sm text-amber-900">
                <Warning size={18} weight="fill" className="inline mr-1" />
                Picking will be blocked from this batch until released.
              </div>
              <div>
                <Label className="overline">Reason / Inspection Notes</Label>
                <Input
                  value={holdReason}
                  onChange={(e) => setHoldReason(e.target.value)}
                  className="mt-1"
                  data-testid="hold-reason-input"
                  autoFocus
                />
              </div>
              <Button
                onClick={submitHold}
                className="w-full bg-red-600 hover:bg-red-700"
                data-testid="confirm-hold-btn"
              >
                <ShieldWarning size={18} className="mr-2" /> Put On Hold
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
